import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { FormSpy } from 'react-final-form';
import { FormState } from 'final-form';

import { KNM_EPGU_NOTIFICATION, KNM_NOTIFICATION } from 'CorePackage/MainModule/routes/routes';
import {
  getNotificationBlockId,
  KNM_NOTIFICATION_TITLE_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
  getKnmSystemType,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';

import { KnmSystemTypesEnum } from '../../MainModule/enums/KnmSystemTypesEnum';
import { IKnmNotificationsForm } from '../interfaces/IKnmNotificationsForm';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { SelectInputFieldAutoOptions } from '../../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { DatePickerField } from '../../../UIPackage/MainModule/components/DatePickerField';
import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { KnmNotificationTypeEnum } from '../../MainModule/enums/KnmNotificationTypeEnum';
import { scrollToBlock } from '../../../CorePackage/MainModule/common/scrollToBlock';
import { IconCheck } from '../../../UIPackage/IconsModule/components/IconCheck';
import styles from './KnmNotifications.module.scss';
import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';
import { MAX_CHARACTERS_ERROR, REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getKnoSupervisionStartAvailableUnplannedActivities } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IKnmTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { KnmKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { KnmTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { IKnmErknmFormRescheduling } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRescheduling';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { makeMaxCharactersValidator } from '../../../CorePackage/MainModule/validators/maxCharacters';
import { IDictionaryProfKindSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryProfKindSelectOption';
import { getKnmPmTypeFormValue } from '../../../KnmPmPackage/MainModule/selectors/knmPmSelectors';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { KnmVersionsEnum } from '../../../APIPackage/KnmModule/enums/KnmVersionsEnum';
import { KnmLateFieldsRemark } from '../../KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

interface IKnmNotificationsProps {
  watchOnly: boolean;
  created: string | undefined;
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  kindKnmCode?: KnmKindCodesEnum;
  rescheduling: IKnmErknmFormRescheduling[] | null | undefined;
  isWatchOnlyByTestUser: boolean;
}

interface IKnmNotificationsStoreProps {
  formRef: IKnmFormRef | undefined;
  systemType: KnmSystemTypesEnum | undefined;
  settings: IAdminSetting[] | undefined;
  availableUnplannedActivities: boolean | null | undefined;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeWaitingCarriedOut: boolean;
  isNeedActSaveOrSign?: boolean;
  pmType?: IDictionaryProfKindSelectOption;
  lawInterface?: LawInterfacesEnum;
}

const KnmNotificationsWithoutStore = ({
  watchOnly,
  created,
  formRef,
  systemType,
  settings,
  availableUnplannedActivities,
  typeCode,
  kindKnmCode,
  rescheduling,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusTypeCarriedOut,
  isWatchOnlyByTestUser,
  isKnmStatusTypeWaitingCarriedOut,
  isNeedActSaveOrSign,
  pmType,
  lawInterface,
}: IKnmNotificationsProps & IKnmNotificationsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isPm = systemType === KnmSystemTypesEnum.PM;
  const isPmTypeOP = pmType?.code === ProfKindCodesEnum.OP;
  const version = lawInterface === LawInterfacesEnum.FZ_248 ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP;

  const addKnmResult = useCallback(
    () =>
      !watchOnly &&
      push &&
      push('subjectNotifications', {
        listId: uuidv4(),
        isNew: true,
        date: undefined,
        type: undefined,
        comment: undefined,
      }),
    [push, watchOnly],
  );
  const makeRemoveKnmResultHandler = (index: number) => () =>
    !watchOnly && remove && remove('subjectNotifications', index);

  const scrollToEpguNotifications = () => {
    scrollToBlock(KNM_EPGU_NOTIFICATION);
  };

  let maxDate: Date | undefined;

  if (rescheduling) {
    const newRescheduling = rescheduling.find(i => i.isNew);

    maxDate = newRescheduling ? new Date() : undefined;
  }

  const isLaterThan4119 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_19_STARTS_FROM,
  );
  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  /**
   * Необходимость показа чекбокса "Не уведомлять контролируемое лицо"
   */
  const isShowCheckboxNotNotifySubject = useMemo(() => {
    return (
      availableUnplannedActivities === true && kindKnmCode === KnmKindCodesEnum.VP && typeCode === KnmTypeCodesEnum.VP
    );
  }, [availableUnplannedActivities]);

  const sectionTitle = useMemo(() => {
    let title = '';

    if (isPm) {
      if (isEnablePaperlessPassportDocument) {
        title = 'Информация об уведомлении проверяемого лица о решении о проведении ПМ';
      } else {
        title = 'Информация об уведомлении проверяемого лица о проведении ПМ';
      }
    } else if (isEnablePaperlessPassportDocument) {
      title = 'Информация об уведомлении проверяемого лица о решении о проведении КНМ';
    } else {
      title = 'Информация об уведомлении проверяемого лица о проведении КНМ';
    }

    return getTitleFieldWithRedStar({
      label: title,
      field: 'subjectNotifications',
      // prettier-ignore
      isPm: (version === KnmVersionsEnum.ERP && isPmTypeOP) ? false : (isPm && !isPmTypeOP),
      isKnm: !isPm,
      sectionName: 'subjectNotifications',
    });
  }, [isPm, isPmTypeOP, version, isEnablePaperlessPassportDocument]);

  const validateNotifications = useCallback(
    (values: IKnmNotificationsForm[] | undefined) => {
      if (
        !isPm &&
        (isKnmStatusTypeCarriedOut || isKnmStatusTypeWaitingCarriedOut) &&
        (!values || values?.length === 0) &&
        kindKnmCode !== KnmKindCodesEnum.IV &&
        kindKnmCode !== KnmKindCodesEnum.MZ &&
        kindKnmCode !== KnmKindCodesEnum.KZ &&
        kindKnmCode !== KnmKindCodesEnum.VK
      ) {
        return REQUIRED_ERROR;
      }
    },
    [isKnmStatusTypeCarriedOut, isKnmStatusTypeWaitingCarriedOut, kindKnmCode, isPm],
  );

  return (
    <Section
      title={sectionTitle}
      id={KNM_NOTIFICATION}
      titleId={KNM_NOTIFICATION_TITLE_BLOCK_ID}
      type="list"
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly || isNeedActSaveOrSign}
          onClick={addKnmResult}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {(props: FormState<IKnmErknmForm>) => {
          const fieldError = props.errors?.subjectNotifications;
          const isFieldTouched = props.touched?.subjectNotifications;

          if (fieldError && isFieldTouched && typeof fieldError === 'string') {
            return <p className="shared-input-error">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      {isShowCheckboxNotNotifySubject && (
        <Row>
          <CheckboxField
            id="notNotifySubject"
            name="notNotifySubject"
            label="Не уведомлять контролируемое лицо"
            theme="system"
            type="checkbox"
            disabled={isWatchOnlyByTestUser || (!isKnmStatusFillInProcess && !isKnmStatusReadyForApprove)}
          />
        </Row>
      )}
      <FieldArray name="subjectNotifications" validate={validateNotifications}>
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmNotificationsForm = fields.value?.[index];
            const id = getNotificationBlockId(fieldValue.listId);
            const { isNew, type, isTypeEmpty, isDateEmpty, isCommentEmpty } = fieldValue;
            const isEpguType = type?.code === KnmNotificationTypeEnum.EPGU;
            const isTypeFieldDisabled =
              watchOnly || !(isNew || (isLaterThan4119 && isTypeEmpty)) || isNeedActSaveOrSign;
            const isDateFieldDisabled =
              watchOnly || !(isNew || (isLaterThan4119 && isDateEmpty)) || isNeedActSaveOrSign;
            const isCommentFieldDisabled =
              watchOnly || !(isNew || (isLaterThan4119 && isCommentEmpty)) || isNeedActSaveOrSign;

            const lateFieldName = `/subjectNotifications/${fieldValue.listId}`;

            return (
              <Row
                id={id}
                isListItem
                key={id}
                align={isEpguType && !isNew ? 'end' : undefined}
                notSpace={isEpguType && !isNew}
              >
                <FieldBlock
                  size="col-3"
                  label={getTitleFieldWithRedStar({
                    label: 'Способ уведомления',
                    field: `${name}.type`,
                    isPm,
                    isKnm: !isPm,
                  })}
                  id={`${id}.type`}
                  tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/type`} fieldId={`${id}.type`} />}
                >
                  <SelectInputFieldAutoOptions
                    id={`${name}.type`}
                    name={`${name}.type`}
                    disabled={isTypeFieldDisabled}
                    size="medium"
                    optionName={CommonCatalogsNamesEnum.KNM_NOTICE_METHODS}
                    validate={!isTypeFieldDisabled ? makeRequired(REQUIRED_ERROR) : () => undefined}
                  />
                </FieldBlock>
                {isEpguType && !isNew ? (
                  <>
                    <Button
                      className={cn('shared-btn-add', styles.ButtonLink)}
                      text="Данные об уведомлении контролируемого лица"
                      type="button"
                      variant="link"
                      color="primary"
                      onClick={scrollToEpguNotifications}
                      disabled={isWatchOnlyByTestUser}
                    />
                    <IconCheck className={styles.IconCheck} />
                  </>
                ) : (
                  <>
                    <FieldBlock
                      size="col-3"
                      label={getTitleFieldWithRedStar({
                        label: 'Дата уведомления',
                        field: `${name}.date`,
                        isPm,
                        isKnm: !isPm,
                      })}
                      id={`${id}.date`}
                      tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/date`} fieldId={`${id}.date`} />}
                    >
                      <DatePickerField
                        name={`${name}.date`}
                        id={`${name}.date`}
                        disabled={isDateFieldDisabled}
                        maxDate={maxDate || new Date()}
                        validate={!isDateFieldDisabled ? makeRequired(REQUIRED_ERROR) : () => undefined}
                      />
                    </FieldBlock>
                    <FieldBlock
                      size="col-3"
                      label={getTitleFieldWithRedStar({
                        label: 'Примечание',
                        field: `${name}.comment`,
                        isPm,
                        isKnm: !isPm,
                      })}
                      id={`${id}.comment`}
                      tooltip={
                        <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/comment`} fieldId={`${id}.comment`} />
                      }
                    >
                      <TextInputField
                        id={`${name}.comment`}
                        name={`${name}.comment`}
                        disabled={isCommentFieldDisabled}
                        validate={composeValidators(
                          !isCommentFieldDisabled ? makeRequired(REQUIRED_ERROR) : () => undefined,
                          makeMaxCharactersValidator(8000, MAX_CHARACTERS_ERROR),
                        )}
                      />
                    </FieldBlock>
                    <CloseButton
                      className={cn('shared-deleting-row-btn', styles.DeleteButton, { [styles.isShowBtnDel]: !isNew })}
                      onClick={makeRemoveKnmResultHandler(index)}
                      disabled={!isNew || isNeedActSaveOrSign}
                    />
                  </>
                )}
              </Row>
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmNotificationsStoreProps => ({
  formRef: getKnmFormRef(state),
  systemType: getKnmSystemType(state),
  settings: getAdminFetchSettings(state),
  availableUnplannedActivities: getKnoSupervisionStartAvailableUnplannedActivities(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeWaitingCarriedOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  pmType: getKnmPmTypeFormValue(state),
  lawInterface: getLawInterface(state),
});

export const KnmNotifications = connect(mapStateToProps)(KnmNotificationsWithoutStore);
